<script lang="ts" setup>
  import type { DeliveryAddressOut } from '@/shared/api/intranet'
  import { useNotifications } from '@/shared/lib'
  import { queryKeys } from '@/shared/lib/queryKeys'
  import { useAppConfig, useComponentConfig } from '@/shared/lib/tenant'
  import { AlertBox } from '@/shared/ui/components'

  import { useBalance } from '@/entities/account'
  import { useProfile } from '@/entities/profile'
  import { ACTUALIZE_CART_ERROR_I18N_KEYS, useCart } from '@/entities/shop'

  import { useCreateOrderDialog } from '@/features/cart/create-order'
  import { getDeliveryAddress } from '@/features/shop/deliveryDetails'

  import { CartDeliveryDetails } from '@/views/cart/delivery-details'
  import { CartItem } from '@/views/cart/item'

  definePage({
    meta: { layout: 'baseNew' },
  })

  const { t } = useI18n()

  useHead({
    title: () => t('cart_heading'),
  })

  const cartStore = useCart()
  const { items, getTotalPrice, getInStockItems, getOutOfStockItems } = storeToRefs(cartStore)
  const { actualizeCart } = cartStore
  const isLoading = ref(true)

  // Срок доставки различается по тенантам; при незаданном meta считаем его месяцами
  const { meta } = useAppConfig()
  const shippingLimit = meta?.shippingLimit === 'weeks' ? 'weeks' : 'months'
  const deliveryTimeText = computed(() => t(`cart_delivery_time_text_${shippingLimit}`))

  const { componentsConfig } = useComponentConfig()
  const coinIcon = computed(() => componentsConfig.value?.badge?.coinIcon)

  const balanceStore = useBalance()
  const { usable: allowedToUseCoins } = storeToRefs(balanceStore)
  const insufficientFunds = computed(() => getTotalPrice.value > allowedToUseCoins.value)
  const { profile } = storeToRefs(useProfile())
  const { errorNotification } = useNotifications()

  const { data: deliveryAddressData, isPending } = useQuery({
    queryKey: queryKeys.getDeliveryAddress(),
    queryFn: () => {
      if (!profile.value) {
        return
      }
      return getDeliveryAddress(profile.value.id)
    },
    enabled: computed(() => Boolean(profile.value)),
  })

  const { open } = useCreateOrderDialog()

  const deliveryDetailsData = computed<DeliveryAddressOut>(() => {
    if (!profile.value) {
      return {
        address: null,
        city: null,
        country: null,
        email: null,
        phone: null,
        postal_code: null,
        user_id: '',
      }
    }
    if (!deliveryAddressData.value || !deliveryAddressData.value.data) {
      return {
        address: null,
        city: null,
        country: null,
        email: null,
        phone: null,
        postal_code: null,
        user_id: profile.value.id,
      }
    }
    return deliveryAddressData.value.data
  })
  const isDetailsNotEnough = computed(() =>
    Object.keys(deliveryDetailsData.value).some(
      (key) => key !== 'user_id' && deliveryDetailsData.value[key as keyof typeof deliveryDetailsData.value] === null,
    ),
  )

  const isCreateOrderButtonDisabled = computed(
    () =>
      isLoading.value ||
      insufficientFunds.value ||
      (getInStockItems.value.length === 0 && getOutOfStockItems.value.length > 0) ||
      isDetailsNotEnough.value,
  )
  const handleCreateOrderDialogOpen = () => {
    open({
      details: computed(() => deliveryDetailsData.value),
    })
  }

  onMounted(async () => {
    balanceStore.fetchBalance()
    try {
      const actualizeError = await actualizeCart()
      if (actualizeError) {
        errorNotification(ACTUALIZE_CART_ERROR_I18N_KEYS[actualizeError])
      }
    } catch (e) {
      errorNotification('shop_cart_actualize_error')
      console.error(e)
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <main class="wide-responsive flex h-full w-full flex-col flex-nowrap!">
    <header class="border-border flex items-center justify-between border-b px-6 pt-8 pb-6">
      <div class="heading-md text-heading!">{{ $t('cart_heading') }}</div>
      <div
        class="flex items-center"
        role="status"
        :aria-label="$t('cart_balance_aria', { amount: allowedToUseCoins })"
      >
        <div class="body-lg-medium">
          {{ allowedToUseCoins }}
        </div>
        <img
          class="ml-1.5 object-contain"
          :src="coinIcon"
          :alt="$t('cart_coin_alt')"
          width="14"
          height="14"
        />
      </div>
    </header>
    <div class="grid grid-cols-[minmax(400px,1fr)_367px] gap-x-10 px-4">
      <template v-if="items.length">
        <section
          class="h-full overflow-hidden"
          :aria-label="$t('cart_section_items_aria')"
        >
          <NScrollbar>
            <ul
              v-if="getInStockItems.length"
              class="divide-border list-none divide-y"
            >
              <li
                v-for="cartItem in getInStockItems"
                :key="cartItem.item.id"
              >
                <CartItem :item="cartItem" />
              </li>
            </ul>
            <template v-if="getOutOfStockItems.length">
              <div class="heading-lg">{{ $t('cart_out_of_stock') }}:</div>
              <ul class="divide-border list-none divide-y pr-3">
                <li
                  v-for="cartItem in getOutOfStockItems"
                  :key="cartItem.item.id"
                >
                  <CartItem :item="cartItem" />
                </li>
              </ul>
            </template>
          </NScrollbar>
        </section>
        <section
          class="flex flex-col flex-nowrap! overflow-hidden pt-6 pl-3"
          :aria-label="$t('cart_section_summary_aria')"
        >
          <NScrollbar>
            <div class="body-xl-semibold mb-2">{{ $t('cart_order_details') }}</div>
            <div class="flex flex-col flex-nowrap! gap-y-4">
              <CartDeliveryDetails
                class="py-2 pr-3"
                :details="deliveryDetailsData"
                :loading="isPending"
              />
              <AlertBox
                type="info"
                :show-icon="false"
                :title="$t('cart_delivery_time_title')"
                :text="deliveryTimeText"
              />
            </div>
          </NScrollbar>
          <div class="mt-auto py-4">
            <div class="heading-md">
              {{ $t('cart_total_amount') }}
              <NTooltip
                placement="top"
                trigger="manual"
                :show="insufficientFunds"
                :theme-overrides="{
                  borderRadius: '8px',
                  padding: '4px 8px',
                  color: 'var(--color-zinc-700)',
                }"
              >
                <template #trigger>
                  <span
                    class="ml-2 inline-flex items-center"
                    :class="{ 'text-rose-600': insufficientFunds }"
                  >
                    {{ cartStore.getTotalPrice }}
                    <img
                      class="ml-1 inline"
                      :src="coinIcon"
                      :alt="$t('cart_coin_alt')"
                      width="16"
                      height="16"
                    />
                  </span>
                </template>
                <span
                  role="alert"
                  class="body-lg-medium"
                >
                  {{ $t('cart_insufficient_funds') }}
                </span>
              </NTooltip>
            </div>
            <div class="mt-4">
              <NButton
                type="primary"
                block
                strong
                :disabled="isCreateOrderButtonDisabled"
                @click="handleCreateOrderDialogOpen"
              >
                {{ $t('cart_order') }}
              </NButton>
            </div>
          </div>
        </section>
      </template>
      <div
        v-else
        class="col-span-2 flex h-full items-center justify-center"
      >
        <div class="flex flex-col flex-nowrap items-center gap-6">
          <img
            src="@/assets/img/no-data.webp"
            :alt="$t('cart_empty_image_alt')"
            width="462"
            height="230"
          />
          <div class="flex flex-col flex-nowrap">
            <div class="body-md">{{ $t('cart_empty_text') }}</div>
            <div class="mt-4 text-center">
              <RouterLink
                v-slot="{ navigate }"
                to="/shop"
                custom
              >
                <NButton
                  type="primary"
                  secondary
                  strong
                  @click="navigate"
                >
                  {{ $t('cart_go_to_shop') }}
                </NButton>
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
